import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'

import { cn } from '@/lib/utils'
import { CheckIcon } from '@/components/Icons'

/**
 * v2 · Option rows — the customize choice pattern.
 *
 * The selectable box is a real radio so the keyboard and screen-reader
 * behaviour comes free; only the skin changes between the two forms:
 *
 *   card — 16–18px padded, title over a sub-line, price on the right
 *   row  — 56pt tall, used inside sheets
 *
 * Rest is the surface with a hairline; selected raises the fill and
 * swaps the hairline for a solid ink border, and a pink check appears
 * on the right — the confirmation is the only colour in the row.
 */

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-2.5', className)} {...props} />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

/** Bare 20px radio — checked shows the pink check, nothing else. */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      'size-5 shrink-0 rounded-full border border-[color:var(--sss-field-border)] bg-[color:var(--sss-field-bg)]',
      'transition-colors duration-[120ms] ease-standard',
      'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
      'data-[state=checked]:border-ink data-[state=checked]:text-signal',
      'disabled:cursor-not-allowed disabled:border-hairline disabled:bg-surface-sunk disabled:opacity-70',
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <CheckIcon size={12} />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
))
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

interface OptionCardProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, 'children' | 'title'> {
  title: React.ReactNode
  /** Fit note, stock note — sits under the title in ink-muted. */
  meta?: React.ReactNode
  /** Price delta, right-aligned. */
  price?: React.ReactNode
  look?: 'card' | 'row'
}

const OptionCard = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  OptionCardProps
>(({ className, title, meta, price, look = 'card', disabled, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    disabled={disabled}
    className={cn(
      'group flex w-full cursor-pointer items-center justify-between gap-4 rounded-md border text-left',
      'border-hairline bg-surface',
      'transition-[background-color,border-color] duration-[120ms] ease-standard',
      'hover:border-[color:var(--sss-field-border)] hover:bg-surface-sunk',
      'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
      'data-[state=checked]:border-ink data-[state=checked]:bg-surface-sunk',
      'disabled:pointer-events-none disabled:border-hairline disabled:bg-surface-sunk disabled:opacity-55',
      look === 'card' ? 'px-5 py-4' : 'h-14 px-4',
      className
    )}
    {...props}
  >
    <span className="flex min-w-0 flex-col gap-[3px] text-left">
      <span className="text-[15px] font-semibold leading-tight">{title}</span>
      {meta && (
        <span className="text-[13px] leading-snug text-ink-faint group-data-[state=checked]:text-ink-muted">
          {meta}
        </span>
      )}
    </span>
    <span className="flex shrink-0 items-center gap-3">
      {price != null && <span className="text-[14px] font-medium text-ink-muted">{price}</span>}
      {/* The pink confirmation — only visible once chosen. */}
      <CheckIcon
        size={20}
        aria-hidden="true"
        className="text-signal group-data-[state=unchecked]:hidden"
      />
    </span>
  </RadioGroupPrimitive.Item>
))
OptionCard.displayName = 'OptionCard'

/**
 * Swatches — 32px circles. Selected takes a 2px page-colour gap then
 * a 2px ink ring; unavailable drops to 40% with a slash. The name is
 * always rendered too, never colour alone.
 */
interface SwatchProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, 'children'> {
  color: string
  name: string
  unavailable?: boolean
  size?: number
}

const Swatch = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, SwatchProps>(
  ({ className, color, name, unavailable, size = 32, disabled, ...props }, ref) => (
    <RadioGroupPrimitive.Item
      ref={ref}
      aria-label={name}
      disabled={disabled || unavailable}
      className={cn(
        'group relative shrink-0 cursor-pointer rounded-full border border-hairline',
        'transition-shadow duration-[120ms] ease-standard',
        'focus-visible:outline-none',
        'data-[state=checked]:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ink)]',
        'focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
        unavailable && 'cursor-not-allowed opacity-40',
        className
      )}
      style={{ width: size, height: size, background: color }}
      {...props}
    >
      {unavailable && (
        <svg
          viewBox="0 0 32 32"
          className="absolute inset-0 size-full text-ink"
          stroke="currentColor"
          strokeWidth="1.2"
          aria-hidden="true"
        >
          <path d="M5 27L27 5" />
        </svg>
      )}
    </RadioGroupPrimitive.Item>
  )
)
Swatch.displayName = 'Swatch'

/**
 * The configurator's mobile colour picker — a 4-up grid of bordered
 * tiles, each a 36px swatch over its name.
 */
interface SwatchTileProps extends SwatchProps {}

const SwatchTile = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  SwatchTileProps
>(({ className, color, name, unavailable, disabled, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    aria-label={name}
    disabled={disabled || unavailable}
    className={cn(
      'group flex cursor-pointer flex-col items-center gap-2 rounded-md border border-hairline bg-surface px-1 py-3',
      'transition-colors duration-[120ms] ease-standard hover:bg-surface-sunk',
      'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
      'data-[state=checked]:border-ink data-[state=checked]:bg-surface-sunk',
      'disabled:pointer-events-none disabled:opacity-40',
      className
    )}
    {...props}
  >
    <span
      aria-hidden="true"
      className={cn(
        'size-9 rounded-full border border-hairline',
        'group-data-[state=checked]:shadow-[0_0_0_2px_var(--sss-surface-sunk),0_0_0_4px_var(--sss-ink)]'
      )}
      style={{ background: color }}
    />
    <span className="text-[12px] font-medium leading-none text-ink-muted group-data-[state=checked]:text-ink">
      {name}
    </span>
  </RadioGroupPrimitive.Item>
))
SwatchTile.displayName = 'SwatchTile'

export { RadioGroup, RadioGroupItem, OptionCard, Swatch, SwatchTile }
